import { Container } from '@/components/atoms';
import { PageBanner } from '@/components/organisms';
import { SERVICE_ARTICLE } from '@/services';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import ArticleCard from './components/ArticleCard';
import Filter from './components/ArticleFilter';

const ArticlePage = () => {
  const { t } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [tags, setTags] = useState([]);
  const [filters, setFilters] = useState({});
  const [filtersName, setFiltersName] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Fetch articles whenever currentPage or filters change
  useEffect(() => {
    const fetchArticles = () => {
      const queryParams = new URLSearchParams({
        page: currentPage,
        size: itemsPerPage,
        ...filters,
      }).toString();

      SERVICE_ARTICLE.getArticleListData(`?${queryParams}`, ({ payload, error, success }) => {
        if (success) {
          setArticles(payload.data);
          setTotalPages(payload.pageable.total);
        } else {
          console.error('Error fetching articles:', error);
        }
      });
    };

    fetchArticles();
  }, [currentPage, filters]);

  // Fetch tags on component mount
  useEffect(() => {
    SERVICE_ARTICLE.getArticleTags(({ payload, error, success }) => {
      if (success) {
        setTags(payload);
      } else {
        console.error('Error fetching tags:', error);
      }
    });
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const findTagNameById = (id) => {
    const tag = tags.find((tg) => tg.id === Number(id));
    return tag ? tag.name : null;
  };

  const applyFilter = (newFilters) => {
    const newFiltersName = [];

    if (newFilters.tag_id) {
      newFiltersName.push(findTagNameById(newFilters.tag_id));
    }
    if (newFilters.time_filter) {
      newFiltersName.push(newFilters.time_filter);
    }
    setFiltersName(newFiltersName);
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  return (
    <>
      <PageBanner title={t('articles_news')} />
      <div className="bg-gray-50">
        <div className="container py-10">
          {/* Header Filter */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex flex-wrap">
              {filtersName
                ? filtersName.map((cat) => (
                    <div className="flex p-2 m-1 border border-gray-200 rounded-xl bg-purple-100 text-purple-700">
                      <div>{cat}</div>
                    </div>
                  ))
                : null}
            </div>
            <div className="ml-auto">
              <Filter onApplyFilter={applyFilter} tags={tags} />
            </div>
          </div>

          {/* Article Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <ArticleCard
                key={article.slug}
                title={article.title}
                banner={article.thumbnail || 'https://placehold.co/600x400?text=Article'}
                createdAt={article.published_at}
                tags={article.tags}
                slug={article.slug}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-8 flex justify-center items-center">
            {/* Previous Button */}
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-full mx-1 flex items-center ${
                currentPage === 1 ? 'text-gray-300' : 'text-purple-600'
              }`}
            >
              <FiChevronLeft />
              <span className="ml-1">Prev</span>
            </button>

            {/* Page Numbers */}
            {[...Array(totalPages).keys()].map((page) => {
              const pageNumber = page + 1;
              return (
                <button
                  type="button"
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`px-4 py-2 mx-1 rounded-full ${
                    pageNumber === currentPage ? 'bg-purple-100 text-purple-700' : 'text-gray-700'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}

            {/* Next Button */}
            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-full mx-1 flex items-center ${
                currentPage === totalPages ? 'text-gray-300' : 'text-purple-600'
              }`}
            >
              <span className="mr-1">Next</span>
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ArticlePage;
